import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, message, Tabs, Tooltip, Spin, Pagination } from 'antd';
import { PictureOutlined } from '@ant-design/icons';
import { ImLink } from 'react-icons/im';
import { getDataRequest } from '../../api/serviceMethods';
import { EVENTS_URL } from '../../api/URLs';
import HeaderComponent from '../../components/header/headerComponent';
import '../../components/dashboard/tabs.css';
import '../../styles/event.css';
import { WEB_ENDPOINT } from '../../config/api';

export const getEventsListApi = async (params = {}) => {
  const query = {
    keyword: params.keyword ?? '',
    startDate: params.startDate ?? '',
    endDate: params.endDate ?? '',
    pageNo: params.pageNo ?? 1,
    pageSize: params.pageSize ?? 10,
    sortKeys: params.sortKeys ?? { createdAt: 'desc' },
    tab: params.tab ?? 'ongoing',
  };
  return await getDataRequest(EVENTS_URL, query);
};

export const normalizeEvent = (item, lang = 'cn') => {
  if (!item) return item;
  const currentLang = lang || 'cn';
  const title =
    item.localize?.[currentLang]?.title ||
    item.localize?.cn?.title ||
    item.localize?.en?.title ||
    item.title ||
    '';

  const poster =
    item.images && item.images.length > 0 && item.images[0]?.url
      ? item.images[0].url
      : item.poster || '';

  const formatDatePart = (isoStr) => {
    if (!isoStr) return '';
    try {
      const dt = new Date(isoStr);
      if (isNaN(dt.getTime())) return isoStr;
      const y = dt.getFullYear();
      const m = String(dt.getMonth() + 1).padStart(2, '0');
      const d = String(dt.getDate()).padStart(2, '0');
      return `${y}.${m}.${d}`;
    } catch {
      return isoStr;
    }
  };

  const time =
    item.startDate && item.endDate
      ? `${formatDatePart(item.startDate)}-${formatDatePart(item.endDate)}`
      : item.time || '';

  return {
    ...item,
    id: item._id || item.eventId || item.id,
    _id: item._id || item.id,
    eventId: item.eventId || item._id,
    title,
    poster,
    time,
    creator: item.createdBy?.name || item.creator || '',
  };
};

const EventManagementPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('ongoing'); // 'ongoing' | 'expired'
  const [rawEvents, setRawEvents] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [keyword] = useState('');
  const [startDate] = useState('');
  const [endDate] = useState('');

  // Fetch events from API with tab parameter
  const fetchEvents = useCallback(
    async (overrides = {}) => {
      setLoading(true);
      const pNo = overrides.pageNo ?? pageNo;
      const pSize = overrides.pageSize ?? pageSize;
      const kw = overrides.keyword ?? keyword;
      const sDate = overrides.startDate ?? startDate;
      const eDate = overrides.endDate ?? endDate;
      const currentTab = overrides.tab ?? activeTab;
      const sKeys = overrides.sortKeys ?? { createdAt: 'desc' };

      try {
        const res = await getEventsListApi({
          keyword: kw,
          startDate: sDate,
          endDate: eDate,
          pageNo: pNo,
          pageSize: pSize,
          sortKeys: sKeys,
          tab: currentTab,
        });

        if (res && res.status === 200 && Array.isArray(res.data)) {
          const lang = i18n.language === 'en' ? 'en' : 'cn';
          const normalized = res.data.map((item) => normalizeEvent(item, lang));
          setRawEvents(res.data);
          setEvents(normalized);
          setTotal(typeof res.total === 'number' ? res.total : normalized.length);
          if (res.pageNo) setPageNo(res.pageNo);
          if (res.pageSize) setPageSize(res.pageSize);
        } else {
          setRawEvents([]);
          setEvents([]);
          setTotal(0);
        }
      } catch (err) {
        console.error('Failed fetching events from API:', err);
        setRawEvents([]);
        setEvents([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    },
    [pageNo, pageSize, keyword, startDate, endDate, activeTab]
  );

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // When language switches, re-normalize in memory without re-calling API or showing loading spinner
  useEffect(() => {
    if (rawEvents && rawEvents.length > 0) {
      const lang = i18n.language === 'en' ? 'en' : 'cn';
      setEvents(rawEvents.map((item) => normalizeEvent(item, lang)));
    }
  }, [i18n.language, rawEvents]);

  const handleTabChange = (key) => {
    setActiveTab(key);
    setPageNo(1);
    fetchEvents({ tab: key, pageNo: 1 });
  };

  const filteredEvents = events;

  const handleCopyLink = (event, e) => {
    e.stopPropagation();
    const eventIdentifier = event?.formLink || event?.id || event?._id || event?.eventId;
    const link =
      event.formLink &&
      (event.formLink.startsWith('http://') || event.formLink.startsWith('https://'))
        ? event.formLink
        : `${WEB_ENDPOINT}/shareform?id=${eventIdentifier}`;
    navigator.clipboard
      .writeText(link)
      .then(() => {
        message.success(t('Registration link copied to clipboard!') || '报名链接已复制到剪贴板！');
      })
      .catch(() => {
        message.success((t('Registration link copied to clipboard!') || '报名链接已复制到剪贴板！') + link);
      });
  };

  const tabItems = [
    {
      key: 'ongoing',
      label: t('Ongoing') || '进行中',
    },
    {
      key: 'expired',
      label: t('Expired') || '已结束',
    },
  ];

  return (
    <HeaderComponent pageTitle={t('Event Management') || '活动管理'}>
      <div className="box-shadow h-100 w-100 event-page-wrapper">
        <div className="w-100 h-100 overflow-auto">
          <div className="w-100">
            <Tabs
              activeKey={activeTab}
              onChange={handleTabChange}
              className="cusStyle event-tabs-wrapper"
              items={tabItems}
              tabBarExtraContent={
                <Button
                  style={{
                    fontSize: 15,
                    backgroundColor: '#F8E2E8',
                    border: 'none',
                    color: '#E50A47',
                    fontWeight: 600,
                    marginRight: 25,
                    borderRadius: 6,
                    height: 36,
                    // cursor: 'pointer',
                    opacity:0.4
                  }}
                  disabled
                  // onClick={() => navigate('/events/upload')}
                >
                  {t('Create Event') || '创建活动'}
                </Button>
              }
            />

            <div style={{ padding: '0px 25px 30px 25px' }}>
              <Spin spinning={loading}>
                <div style={{ minHeight: 280 }}>
                  {/* Events Grid */}
                  <div className="event-cards-grid">
                    {filteredEvents.map((event) => {
                      const eventId = event.id || event._id;
                      const isExpired = activeTab === 'expired';

                      return (
                        <div key={eventId} className="event-card">
                          <div className="event-card-header">
                            {/* <Tooltip title={t('Click to edit event and poster') || '点击编辑活动与海报'}> */}
                              <div
                                style={{ position: 'relative', flexShrink: 0 }}
                                // onClick={() => navigate(`/events/upload/${eventId}`)}
                              >
                                <img
                                  src={event.poster}
                                  alt={event.title}
                                  className="event-card-thumb"
                                  onError={(e) => {
                                    e.target.onerror = null;
                                  }}
                                />
                                <div
                                  style={{
                                    position: 'absolute',
                                    inset: 0,
                                    background: 'rgba(0,0,0,0.3)',
                                    borderRadius: 6,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    opacity: 0,
                                    transition: 'opacity 0.2s',
                                  }}
                                  // className="poster-hover-overlay"
                                  // onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
                                  // onMouseLeave={(e) => (e.currentTarget.style.opacity = '0')}
                                >
                                  {/* <PictureOutlined style={{ color: '#fff', fontSize: 16 }} /> */}
                                </div>
                              </div>
                            {/* </Tooltip> */}

                            <div className="event-card-body">
                              <div className="event-card-title-row">
                                <h3 className="event-card-title">{event.localize?.cn?.title || event.title}</h3>
                                <span
                                  className={`event-status-tag ${
                                    isExpired ? 'expired' : 'active'
                                  }`}
                                >
                                  {isExpired ? (t('Expired') || '已结束') : (t('Ongoing') || '进行中')}
                                </span>
                              </div>
                              <div className="event-card-meta">
                                <span style={{ color: '#262626' }}>
                                  {t('Event Time') || '活动时间'}: {event.time}
                                </span>
                                <span>
                                  {t('Creator') || '创建人'}: {event.creator}
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className="event-card-divider" />

                          <div className="event-card-actions">
                            <Button
                              className="btn-event-outline"
                              disabled
                              style={{opacity:0.4}}
                              // onClick={() => navigate(`/events/upload/${eventId}?tab=form`)}
                            >
                              {t('Build Registration Form') || '制作报名表单'}
                            </Button>
                            <Button
                              className="btn-event-outline"
                              onClick={() =>
                                navigate(`/events/${eventId}`, {
                                  state: { event: { ...event, isExpired }, tab: activeTab },
                                })
                              }
                            >
                              {t('Registration Approval') || '报名审核'}
                            </Button>
                            <Button
                              className="btn-event-outline"
                              onClick={() =>
                                navigate(`/events/${eventId}/admission`, {
                                  state: { event: { ...event, isExpired }, tab: activeTab },
                                })
                              }
                            >
                              {t('Event Admission') || '活动核销'}
                            </Button>
                          </div>

                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span
                              className="event-card-link"
                              onClick={(e) => handleCopyLink(event, e)}
                            >
                              <ImLink /> {t('Copy Registration Link') || '复制报名链接'}
                            </span>
                          </div>
                        </div>
                      );
                    })}

                    {!loading && filteredEvents.length === 0 && (
                      <div
                        style={{
                          gridColumn: '1 / -1',
                          textAlign: 'center',
                          padding: '60px 0',
                          color: '#636E88',
                        }}
                      >
                        {t('No event data') || '暂无活动数据'}
                      </div>
                    )}
                  </div>

                  {total > pageSize && (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        marginTop: 24,
                      }}
                    >
                      <Pagination
                        current={pageNo}
                        pageSize={pageSize}
                        total={total}
                        onChange={(page, size) => {
                          setPageNo(page);
                          setPageSize(size);
                          fetchEvents({ pageNo: page, pageSize: size });
                        }}
                        showSizeChanger
                      />
                    </div>
                  )}
                </div>
              </Spin>
            </div>
          </div>
        </div>
      </div>
    </HeaderComponent>
  );
};

export default EventManagementPage;
